import { useCustomerDetails } from '@egw/api'
import { PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { formatDateNoTZ } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EditMaritalStatus, EditPoliticallyExposedPerson } from '../../components'
import { EditAddressField } from '../../components/edit-address-field/edit-address-field'
import { EditAnnualIncomeField } from '../../components/edit-annual-income-field/edit-annual-income-field'
import { EditBrokerageAffiliationField } from '../../components/edit-brokerage-affiliation-field/edit-brokerage-affiliation-field'
import { EditEmploymentStatusField } from '../../components/edit-employment-status-field/edit-employment-status-field'
import { EditExecutiveOrShareholder } from '../../components/edit-executive-field/edit-executive-field'
import { EditInvestmentExperience } from '../../components/edit-investment-experience/edit-investment-experience'
import { EditInvestmentGoalField } from '../../components/edit-investment-goal-field/edit-investment-goal-field'
import { EditNetWorthField } from '../../components/edit-net-worth-field/edit-net-worth-field'
import { EditOccupationField } from '../../components/edit-occupation-field/edit-occupation-field'
import { EditTaxFilingStatusField } from '../../components/edit-tax-filing-field/edit-tax-filing-field'
import { EditEmailField } from '../../components/update-email/edit-email-field'
import { EditPhoneField } from '../../components/update-phone/edit-phone-field'

export const PersonalInfoPage: FC = () => {
  const { t } = useTranslation('features-settings')

  const { data, isLoading } = useCustomerDetails()

  // formatDateNoTz
  const clientSinceDate = data?.customer.createdDate
    ? formatDateNoTZ(data.customer.createdDate, t('date.medium'))
    : undefined

  const name = (() => {
    if (!data?.customer.name) return undefined

    return `${data.customer.name.firstName} ${data.customer.name.lastName}`
  })()

  const isShowEmploymentTitle =
    data?.customer.employmentStatus ??
    data?.customer.occupation ??
    data?.customer.isBrokerDealer ??
    data?.customer.isDirectorPolicyMakerOfCompany

  const isShowFinancialTitle =
    data?.customer.maritalStatus ??
    data?.customer.personalNetWorthRange ??
    data?.customer.personalAnnualGrossIncome ??
    data?.customer.taxFilingStatus ??
    data?.customer.investmentObjective ??
    data?.customer.investmentExperience

  if (isLoading) {
    return (
      <div className="egw-container p-xs bg-primary-500 typo-head-1">
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        subtitle={t('settings.title')}
        title={t('personal_info.title')}
        backLinkUrl={WEALTH_ROUTES.SETTINGS_PERSONAL_INFO.backPath}
      />
      <div className="egw-container flex flex-col gap-8">
        {/** NAME  */}
        <div className="border-b border-light  pb-3 flex flex-col md:flex-row justify-between items-baseline">
          <span className="typo-primary-2xl text-header">{name}</span>
          <span className="typo-primary-md-light text-header font-light">
            {t('personal_info.client_since', { date: clientSinceDate })}
          </span>
        </div>

        {/** Contact Information */}
        <div className="pb-3 flex flex-col justify-between items-baseline">
          <span className="typo-primary-md-semibold text-header font-semibold mb-4">
            {t('personal_info.contact_information')}
          </span>
          <EditEmailField />
          <EditPhoneField />
          <EditAddressField />
        </div>

        {/** Employment Information */}
        <div className="pb-3 flex flex-col justify-between items-baseline">
          {isShowEmploymentTitle ? (
            <span className="typo-primary-md-semibold text-header font-semibold mb-4">
              {t('personal_info.employment')}
            </span>
          ) : null}
          <EditEmploymentStatusField />
          <EditOccupationField />
          <EditBrokerageAffiliationField />
          <EditExecutiveOrShareholder />
          <EditPoliticallyExposedPerson />
        </div>

        {/** Financial */}
        <div className="pb-3 flex flex-col justify-between items-baseline">
          {isShowFinancialTitle ? (
            <span className="typo-primary-md-semibold text-header font-semibold mb-4">
              {t('personal_info.financial')}
            </span>
          ) : null}
          <EditMaritalStatus />
          <EditNetWorthField />
          <EditAnnualIncomeField />
          <EditTaxFilingStatusField />
          <EditInvestmentGoalField />
          <EditInvestmentExperience />
        </div>
      </div>
    </>
  )
}
